import axios from "axios";
import Cookies from "js-cookie";
import { getCallCenterBaseUrl, requestCallCenter, unwrapEnvelopeValue, downloadBlobAsFile } from "./CallCenterApi";
import { isCallingEnabled } from "../utils/orgFeatures";
import { ORGANIZATION_HEADER } from "../utils/organizationContext";
import { cachedRequest, invalidateCachedRequest } from "./callCenterRequestCache";

const CACHE_KEYS = {
  columns: "tickets:columns",
  workspaceInit: "tickets:workspace-init",
  officeUsers: "calls:office-users",
};

const withOptionalNumber = (value) =>
  value === "" || value === null || typeof value === "undefined"
    ? undefined
    : Number(value);

/**
 * @typedef {Object} TicketListFilters
 * @property {number} [page]
 * @property {number} [size]
 * @property {string} [accountNo]
 * @property {string} [accountId]
 * @property {boolean} [called]
 * @property {Array<number|string>|string} [statusIds]
 * @property {number|string} [minOutstanding]
 * @property {number|string} [maxOutstanding]
 * @property {string} [fromPaymentDate] yyyy-MM-dd
 * @property {string} [toPaymentDate] yyyy-MM-dd
 * @property {"OUTSTANDING_ASC"|"OUTSTANDING_DESC"|"PAYMENT_DATE_ASC"|"PAYMENT_DATE_DESC"|"CALLED_AT_ASC"|"CALLED_AT_DESC"|"UPDATED_DESC"} [sortBy]
 * @property {number|string} [assigneeId]
 */

/**
 * @typedef {Object} TicketListPage
 * @property {Array<Object>} items
 * @property {number} totalElements
 * @property {number} totalPages
 * @property {number} page
 * @property {number} size
 * @property {boolean} hasNext
 * @property {boolean} hasPrevious
 */

export const createTicket = async (payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to create ticket");
};

export const createBulkTickets = async (payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/bulk",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to create bulk tickets");
};

export const uploadBulkTickets = async ({
  file,
  priority,
  stopOnError = false,
  columnMappings,
  reassignExisting = false,
}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("priority", priority);
  formData.append("stopOnError", String(Boolean(stopOnError)));
  formData.append("reassignExisting", String(Boolean(reassignExisting)));
  if (columnMappings && Object.keys(columnMappings).length > 0) {
    formData.append("columnMappings", JSON.stringify(columnMappings));
  }
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/bulk/upload",
    data: formData,
    timeout: 10 * 60 * 1000,
  });
  const result = unwrapEnvelopeValue(envelope, "Failed to upload bulk ticket file");
  invalidateCachedRequest(CACHE_KEYS.columns, CACHE_KEYS.workspaceInit);
  return result;
};

export const previewBulkUploadHeaders = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/bulk/upload/preview",
    data: formData,
  });
  return unwrapEnvelopeValue(envelope, "Failed to preview upload headers");
};

const fetchTicketColumnConfig = async (organizationId) => {
  const headers = organizationId ? { [ORGANIZATION_HEADER]: String(organizationId) } : undefined;
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets/columns",
    headers,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket column config");
};

export const getTicketColumnConfig = async () =>
  cachedRequest(CACHE_KEYS.columns, () => fetchTicketColumnConfig());

export const getTicketColumnConfigForOrganization = async (organizationId) => {
  if (organizationId == null || organizationId === "") {
    return getTicketColumnConfig();
  }
  const cacheKey = `${CACHE_KEYS.columns}:org:${organizationId}`;
  return cachedRequest(cacheKey, () => fetchTicketColumnConfig(organizationId));
};

export const getAgentWorkspaceInit = async () =>
  cachedRequest(CACHE_KEYS.workspaceInit, async () => {
    const envelope = await requestCallCenter({
      method: "get",
      url: "tickets/workspace-init",
    });
    return unwrapEnvelopeValue(envelope, "Failed to load agent workspace data");
  });

export const updateTicketColumnConfig = async (payload) => {
  const save = async (method, url) => {
    const envelope = await requestCallCenter({
      method,
      url,
      data: payload,
    });
    return unwrapEnvelopeValue(envelope, "Failed to update ticket column config");
  };

  try {
    const result = await save("post", "tickets/columns/save");
    invalidateCachedRequest(CACHE_KEYS.columns, CACHE_KEYS.workspaceInit);
    return result;
  } catch (error) {
    if (error.httpStatus === 404 || error.httpStatus === 405) {
      const result = await save("put", "tickets/columns");
      invalidateCachedRequest(CACHE_KEYS.columns, CACHE_KEYS.workspaceInit);
      return result;
    }
    throw error;
  }
};

export const listTicketUploadLogs = async () => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets/uploads",
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket upload history");
};

export const getTicketUploadLog = async (uploadLogId) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/uploads/${uploadLogId}`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket upload status");
};

export const assignTicket = async ({ id, assignedTo }) => {
  const envelope = await requestCallCenter({
    method: "patch",
    url: `tickets/${id}/assignment`,
    data: { assignedTo: Number(assignedTo) },
  });
  return unwrapEnvelopeValue(envelope, "Failed to assign ticket");
};

export const unassignTicket = async ({ id }) => {
  const envelope = await requestCallCenter({
    method: "delete",
    url: `tickets/${id}/assignment`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to unassign ticket");
};

export const bulkAssignTickets = async ({ ticketIds, assignedTo }) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/bulk/assign",
    data: {
      ticketIds,
      assignedTo: Number(assignedTo),
    },
  });
  return unwrapEnvelopeValue(envelope, "Failed to assign tickets");
};

export const getAssignableOfficeUsers = async ({ refresh = false } = {}) => {
  if (refresh) {
    invalidateCachedRequest(CACHE_KEYS.workspaceInit);
  }
  const init = await getAgentWorkspaceInit();
  return Array.isArray(init?.officeUsers) ? init.officeUsers : [];
};

/** Loads assignable agents for ticket assignment (bypasses stale workspace-init cache). */
export const loadAssignableAgentsForAssignment = async () => {
  try {
    const envelope = await requestCallCenter({
      method: "get",
      url: "tickets/assignable-agents",
    });
    const fromTickets = unwrapEnvelopeValue(envelope, "Failed to load assignable agents");
    if (Array.isArray(fromTickets) && fromTickets.length > 0) {
      return fromTickets;
    }
  } catch {
    // Fall through to legacy sources.
  }
  try {
    const envelope = await requestCallCenter({
      method: "get",
      url: "tickets/workspace-init",
    });
    const init = unwrapEnvelopeValue(envelope, "Failed to load assignable agents");
    const fromWorkspace = Array.isArray(init?.officeUsers) ? init.officeUsers : [];
    if (fromWorkspace.length > 0) {
      return fromWorkspace;
    }
  } catch {
    // Fall through to calls endpoint when workspace-init is unavailable.
  }
  if (isCallingEnabled()) {
    try {
      const envelope = await requestCallCenter({
        method: "get",
        url: "calls/office-users",
      });
      const fromCalls = unwrapEnvelopeValue(envelope, "Failed to load assignable agents");
      return Array.isArray(fromCalls) ? fromCalls : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const bulkSoftDeleteTickets = async ({ ticketIds }) => {
  const envelope = await requestCallCenter({
    method: "delete",
    url: "tickets/bulk",
    data: { ticketIds },
  });
  return unwrapEnvelopeValue(envelope, "Failed to bulk delete tickets");
};

export const listTickets = async ({ accountNo, assigned } = {}) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets",
    params: {
      ...(accountNo ? { accountNo } : {}),
      ...(assigned === true || assigned === false ? { assigned } : {}),
    },
  });
  return unwrapEnvelopeValue(envelope, "Failed to list tickets");
};

/**
 * @param {TicketListFilters} filters
 * @returns {Promise<TicketListPage|Array<Object>>}
 */
export const getTicket = async (ticketId) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/${ticketId}`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket");
};

export const listTicketAssignmentHistory = async (ticketId) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/${ticketId}/assignment-history`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket assignment history");
};

export const listTicketsV2 = async (filters = {}) => {
  const statusIdsParam = Array.isArray(filters.statusIds)
    ? filters.statusIds.filter(Boolean).join(",")
    : filters.statusIds;
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets",
    params: {
      ...(filters.accountNo ? { accountNo: filters.accountNo } : {}),
      ...(filters.accountId ? { accountId: filters.accountId } : {}),
      ...(filters.called === true || filters.called === false
        ? { called: filters.called }
        : {}),
      ...(statusIdsParam ? { statusIds: statusIdsParam } : {}),
      ...(typeof filters.minOutstanding !== "undefined"
        ? { minOutstanding: withOptionalNumber(filters.minOutstanding) }
        : {}),
      ...(typeof filters.maxOutstanding !== "undefined"
        ? { maxOutstanding: withOptionalNumber(filters.maxOutstanding) }
        : {}),
      ...(filters.fromPaymentDate ? { fromPaymentDate: filters.fromPaymentDate } : {}),
      ...(filters.toPaymentDate ? { toPaymentDate: filters.toPaymentDate } : {}),
      ...(filters.sortBy ? { sortBy: filters.sortBy } : {}),
      ...(typeof filters.assigneeId !== "undefined" && filters.assigneeId !== ""
        ? { assigneeId: withOptionalNumber(filters.assigneeId) }
        : {}),
      ...(typeof filters.page === "number" ? { page: filters.page } : {}),
      ...(typeof filters.size === "number" ? { size: filters.size } : {}),
      ...Object.entries(filters)
        .filter(([key, value]) => key.startsWith("customField.") && value)
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {}),
      ...(filters.priority ? { priority: filters.priority } : {}),
      ...(filters.remarks ? { remarks: filters.remarks } : {}),
    },
  });
  return unwrapEnvelopeValue(envelope, "Failed to list tickets");
};

/**
 * @param {{
 *   id: string,
 *   status?: string,
 *   priority?: string,
 *   remarksComment?: string,
 *   additionalComments?: string,
 *   targetOutstanding?: number|string,
 *   targetPaymentDate?: string,
 *   calledAt?: string,
 * }} payload
 */
export const updateTicketStatus = async ({
  id,
  status,
  priority,
  remarksComment,
  additionalComments,
  targetOutstanding,
  targetPaymentDate,
  calledAt,
  customFields,
}) => {
  const envelope = await requestCallCenter({
    method: "patch",
    url: `tickets/${id}/status`,
    data: {
      ...(status ? { status } : {}),
      ...(priority ? { priority } : {}),
      // Send text fields whenever provided (including empty strings) so they can be cleared.
      ...(typeof remarksComment === "string" ? { remarksComment } : {}),
      ...(typeof additionalComments === "string" ? { additionalComments } : {}),
      ...(typeof targetOutstanding !== "undefined" && targetOutstanding !== ""
        ? { targetOutstanding: Number(targetOutstanding) }
        : {}),
      ...(targetPaymentDate ? { targetPaymentDate } : {}),
      ...(calledAt ? { calledAt } : {}),
      ...(customFields && typeof customFields === "object" ? { customFields } : {}),
    },
  });
  return unwrapEnvelopeValue(envelope, "Failed to update ticket status");
};

export const updateTicketStatusSelf = async ({ id, status }) => {
  const envelope = await requestCallCenter({
    method: "patch",
    url: `tickets/${id}/status/self`,
    data: { status },
  });
  return unwrapEnvelopeValue(envelope, "Failed to update own ticket status");
};

export const createTicketStatus = async (payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/statuses",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to save ticket status");
};

export const listTicketStatuses = async (options = {}) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets/statuses",
    params: options.includeInactive ? { includeInactive: true } : undefined,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket statuses");
};

export const updateTicketStatusDefinition = async ({ id, ...payload }) => {
  const envelope = await requestCallCenter({
    method: "put",
    url: `tickets/statuses/${id}`,
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to update ticket status");
};

export const deleteTicketStatusDefinition = async (id) => {
  const envelope = await requestCallCenter({
    method: "delete",
    url: `tickets/statuses/${id}`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to delete ticket status");
};

export const refreshTicketOutstanding = async ({ id }) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${id}/outstanding/refresh`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to refresh outstanding");
};

export const getTicketCallThread = async (ticketId) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/${ticketId}/thread`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket call thread");
};

export const getTicketWhatsAppThread = async (ticketId, phoneNumber) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/${ticketId}/whatsapp-thread`,
    params: phoneNumber ? { phoneNumber } : undefined,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load WhatsApp thread");
};

export const listTicketWhatsAppPhones = async (ticketId) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/${ticketId}/whatsapp/phones`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load WhatsApp phone numbers");
};

export const checkTicketWhatsAppPhone = async (ticketId, phoneNumber) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${ticketId}/whatsapp/check-phone`,
    data: { phoneNumber },
  });
  return unwrapEnvelopeValue(envelope, "Failed to verify WhatsApp number");
};

export const sendTicketWhatsAppMessage = async (ticketId, payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${ticketId}/whatsapp/messages`,
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to send WhatsApp message");
};

export const sendTicketWhatsAppMedia = async (ticketId, { phoneNumber, file, caption }) => {
  const formData = new FormData();
  formData.append("phoneNumber", phoneNumber);
  formData.append("file", file);
  if (caption) {
    formData.append("caption", caption);
  }
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${ticketId}/whatsapp/messages/media`,
    data: formData,
  });
  return unwrapEnvelopeValue(envelope, "Failed to send WhatsApp attachment");
};

export const downloadTicketWhatsAppMedia = async (ticketId, messageId, fileName) => {
  const token = Cookies.get("accessToken");
  const response = await axios.get(
    `${getCallCenterBaseUrl()}api/tickets/${ticketId}/whatsapp/messages/${messageId}/download`,
    {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  const blob = response.data;
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "attachment";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const listTicketAttachments = async (ticketId) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/${ticketId}/attachments`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket attachments");
};

export const uploadTicketAttachment = async (ticketId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${ticketId}/attachments`,
    data: formData,
  });
  return unwrapEnvelopeValue(envelope, "Failed to upload ticket attachment");
};

export const downloadTicketAttachment = async (ticketId, attachmentId, fileName) => {
  const token = Cookies.get("accessToken");
  const response = await axios.get(
    `${getCallCenterBaseUrl()}api/tickets/${ticketId}/attachments/${attachmentId}/download`,
    {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  const blob = response.data;
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "attachment";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const deleteTicketAttachment = async (ticketId, attachmentId) => {
  const envelope = await requestCallCenter({
    method: "delete",
    url: `tickets/${ticketId}/attachments/${attachmentId}`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to delete ticket attachment");
};

export const getTicketPaymentProof = async (ticketId) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: `tickets/${ticketId}/payment-proof`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to load payment proof");
};

export const saveTicketPaymentProofDraft = async (ticketId, fieldData) => {
  const envelope = await requestCallCenter({
    method: "put",
    url: `tickets/${ticketId}/payment-proof/draft`,
    data: { fieldData },
  });
  return unwrapEnvelopeValue(envelope, "Failed to save payment proof draft");
};

export const saveTicketPaymentProof = async (ticketId, { file, fieldData }) => {
  const formData = new FormData();
  formData.append("file", file);
  if (fieldData) {
    formData.append("fieldData", JSON.stringify(fieldData));
  }
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${ticketId}/payment-proof`,
    data: formData,
  });
  return unwrapEnvelopeValue(envelope, "Failed to save payment proof");
};

export const downloadTicketPaymentProof = async (ticketId, fileName) => {
  await downloadBlobAsFile({
    url: `${getCallCenterBaseUrl()}api/tickets/${ticketId}/payment-proof/download`,
    fileName: fileName || "clearance-certificate.pdf",
    fallbackMessage: "Failed to download payment proof",
  });
};

export const sendTicketPaymentProofWhatsApp = async (ticketId, payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${ticketId}/payment-proof/send-whatsapp`,
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to send payment proof via WhatsApp");
};

export const getTeamProgress = async () => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "callcenter/analytics/team-progress",
  });
  return unwrapEnvelopeValue(envelope, "Failed to load team progress");
};
